import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Skeleton,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useNavigate } from 'react-router-dom';
import { getSuggestedFlows, FlowSuggestion } from '../services/documentFlowService';

interface FlowSuggestionCardProps {
  context: string;
  conversationContext?: string;
  sessionId?: string;
  onDismiss: () => void;
}

const FlowSuggestionCard: React.FC<FlowSuggestionCardProps> = ({
  context,
  conversationContext,
  sessionId,
  onDismiss,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [suggestedFlows, setSuggestedFlows] = useState<FlowSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNoFlows, setHasNoFlows] = useState(false);
  const [detectedArea, setDetectedArea] = useState<string | null>(null);
  const onDismissRef = useRef(onDismiss);
  
  // Manter a ref atualizada
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchSuggestions = async () => {
      setIsLoading(true);
      setHasNoFlows(false);
      try {
        // Passa o contexto da conversa (últimas mensagens) além da última
        // pergunta do usuário. A última pergunta sozinha ("como faço aqui")
        // pode não ter nenhum termo jurídico — o contexto anterior é o que
        // dá signal ao ranker (ex.: se antes ele estava analisando o preço
        // de um produto, o próximo "abrir uma causa" é claramente JEC).
        const combinedContext = conversationContext
          ? `${conversationContext}\n\n${context}`
          : context;
        const flows = await getSuggestedFlows(combinedContext, 3);
        if (isMounted) {
          setSuggestedFlows(flows);
          // Extract detected area from the first flow if available
          if (flows.length > 0) {
            const firstFlow = flows[0];
            if (firstFlow._detected_area) {
              setDetectedArea(firstFlow._detected_area);
            } else if (firstFlow.area) {
              // Fallback to first flow's area
              setDetectedArea(firstFlow.area);
            }
          }
          // Só marcar como sem fluxos se a API retornou array vazio (não erro)
          if (Array.isArray(flows) && flows.length === 0) {
            setHasNoFlows(true);
          }
        }
      } catch (error) {
        console.error('Error fetching flow suggestions:', error);
        // Em caso de erro, NÃO dismiss - manter o card com mensagem alternativa
        if (isMounted) {
          setSuggestedFlows([]);
          // NÃO setHasNoFlows(true) para não dismiss em caso de erro
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (context) {
      fetchSuggestions();
    }
    
    return () => {
      isMounted = false;
    };
  }, [context]);
  
  // Dismiss separado - só quando explicitamente não há fluxos
  useEffect(() => {
    if (hasNoFlows && !isLoading) {
      // Delay pequeno para dar tempo do usuário ver a mensagem
      const timer = setTimeout(() => {
        onDismissRef.current();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [hasNoFlows, isLoading]);

  const handleFlowClick = (flow: FlowSuggestion) => {
    navigate(`/documents/flow/${flow.slug}`, {
      state: {
        context: conversationContext,
        fromChat: true,
        sessionId,
      },
    });
  };

  const handleViewAllFlows = () => {
    // Navigate to flows page with area filter if detected
    if (detectedArea) {
      navigate(`/documents/flows?area=${encodeURIComponent(detectedArea)}`);
    } else {
      navigate('/documents/flows');
    }
  };

  const getIconForArea = (area: string): string => {
    const areaIcons: Record<string, string> = {
      civel: '⚖️',
      trabalhista: '👷',
      criminal: '🔒',
      familia: '👨‍👩‍👧',
      consumidor: '🛒',
      tributario: '💰',
      previdenciario: '🏛️',
      contratos: '📝',
    };
    return areaIcons[area.toLowerCase()] || '📄';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3,
        backgroundColor: alpha('#FF8F4E', 0.08),
        borderRadius: 3,
        maxWidth: '85%',
        mb: 2,
        border: `2px solid ${alpha('#FF8F4E', 0.3)}`,
        boxShadow: `0 4px 20px ${alpha('#FF8F4E', 0.15)}`,
        animation: 'slideIn 0.4s ease-out',
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translateX(-20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ color: 'white', fontSize: 24 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#EB5D36',
              mb: 0.5,
            }}
          >
            💡 Sugestão da Advogada Parceira
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            {suggestedFlows.length === 1 
              ? `Analisando nossos bancos de dados, encontrei o fluxo "${suggestedFlows[0].name}" que pode resolver a sua situação:`
              : 'Analisando nossos bancos de dados, encontrei o que você precisa para resolver a sua situação. Veja minhas sugestões abaixo:'}
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={56}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          ) : suggestedFlows.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {suggestedFlows.map((flow) => (
                <Box
                  key={flow.id}
                  onClick={() => handleFlowClick(flow)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'white',
                    border: `1px solid ${alpha('#FF8F4E', 0.3)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: '#EB5D36',
                      bgcolor: alpha('#FF8F4E', 0.05),
                      transform: 'translateX(4px)',
                      boxShadow: `0 4px 12px ${alpha('#FF8F4E', 0.2)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1.5,
                      bgcolor: alpha('#FF8F4E', 0.1),
                      fontSize: '1.2rem',
                    }}
                  >
                    {flow.icon || getIconForArea(flow.area)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, maxWidth: 'calc(100% - 60px)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {flow.name}
                      </Typography>
                      {flow.is_approved && (
                        <VerifiedIcon
                          sx={{
                            fontSize: 14,
                            color: theme.palette.success.main,
                          }}
                        />
                      )}
                    </Box>
                    {flow.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          maxWidth: '100%',
                        }}
                      >
                        {flow.description.length > 60 
                          ? `${flow.description.substring(0, 60)}...` 
                          : flow.description}
                      </Typography>
                    )}
                  </Box>
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 18,
                      color: '#EB5D36',
                      opacity: 0.7,
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mb: 2 }}
            >
              Explore nossos fluxos de documentos disponíveis.
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {suggestedFlows.length === 1 ? (
              <Button
                variant="contained"
                startIcon={<DescriptionIcon />}
                onClick={() => handleFlowClick(suggestedFlows[0])}
                sx={{
                  background: 'linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(235, 93, 54, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d54d2a 0%, #f07d3c 100%)',
                    boxShadow: '0 6px 16px rgba(235, 93, 54, 0.4)',
                  },
                }}
              >
                Usar {suggestedFlows[0].name}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<DescriptionIcon />}
                onClick={handleViewAllFlows}
                sx={{
                  background: 'linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(235, 93, 54, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d54d2a 0%, #f07d3c 100%)',
                    boxShadow: '0 6px 16px rgba(235, 93, 54, 0.4)',
                  },
                }}
              >
                Ver outras opções
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={onDismiss}
              sx={{
                borderColor: '#FF8F4E',
                color: '#EB5D36',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#EB5D36',
                  backgroundColor: alpha('#FF8F4E', 0.05),
                },
              }}
            >
              Continuar a conversa
            </Button>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={onDismiss}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: '#EB5D36',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default FlowSuggestionCard;
